import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import RakenneosaSelect from './RakenneosaSelect';
import TaulukkoFooter from './TaulukkoFooter';
import { JULKISIVU_RAKENNEOSA_OPTIONS, PORANÄYTE_TESTI_OPTIONS } from '../../../assets/julkisivuRakenneosaOptions';
import KohdeTabBar from './KohdeTabBar';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';
import { useRakennusData } from '../../../context/RakennusDataContext';
import { defaultProjectName } from '../../../utils/projectNaming';
import { mittausCacheSet, mittausCacheSubscribe, naytteetCacheSubscribe, naytteetCacheSet, naytteetCacheAddSubscribe, naytteetListSet, naytteetListGet, broadcastNayteTunnus } from '../../../utils/mittausCache';
import { findLinkedProjectId } from '../../../utils/ptsUtils';
import {
  getDynamicPTSProjects,
  getMittausProjektitByPTS,
  createMittausProjekti,
  getMittausProjekti,
  getSiblingMittausProjekti,
  renameMittausProjekti,
  deleteMittausProjekti,
  deleteMittausProjektiWithSiblings,
  updateMittausProjektiMeta,
  createMittausKohde,
  updateMittausKohde,
  getNaytteetByProjekti,
  createNayte,
  updateNayte,
  deleteNayte,
  syncNayte,
} from '../../../api/api';

const C   = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.82rem', verticalAlign: 'top', wordBreak: 'break-word', hyphens: 'auto' };
const CL  = { border: '1px solid #adb5bd', padding: '6px 8px', fontSize: '0.82rem', verticalAlign: 'middle', textAlign: 'center', background: '#f8f9fa', fontWeight: 600, whiteSpace: 'pre-line', width: 130, hyphens: 'auto' };
const CH  = { border: '1px solid #adb5bd', padding: '5px 4px', background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', fontSize: '0.75rem', verticalAlign: 'middle' };
const CG  = { border: '1px solid #adb5bd', padding: '4px 8px', background: '#04aa00', color: '#fff', fontWeight: 700, fontSize: '0.78rem' };
const CG2 = { ...CG, background: '#038800' };

const KARB_PINTA_OPTIONS_UPPER = ['yläpinta', 'ulkopinta', 'rp0'];
const KARB_PINTA_OPTIONS_LOWER = ['alapinta', 'sisäpinta', 'rp1'];

// Rakenneosa-based defaults for carbonation slots.
// upper/lower = surface label for each slot.
// karb_to_table = which slot's average flows to the carbonation summary table.
// Laatta/palkki: yläpinta(upper) / alapinta(lower), kriittinen = alapinta → 'lower'.
// Seinä/kaide: sisäpinta(upper) / ulkopinta(lower), kriittinen = ulkopinta → 'lower'.
const KARB_RAKENNEOSA_DEFAULTS = [
  // Laatta: yläpinta (upper) / alapinta (lower), kriittinen = alapinta → 'lower'
  { match: 'parvekelaatta', upper: 'yläpinta',  lower: 'alapinta',  karb_to_table: 'lower' },
  // Muut: ulkopinta (upper) / sisäpinta (lower), kriittinen = ulkopinta → 'upper'
  { match: 'parvekekaide',  upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'parvekepieli',  upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'julkisivuelementti', upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'sokkeli',       upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'pilari',        upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
  { match: 'palkki',        upper: 'ulkopinta', lower: 'sisäpinta', karb_to_table: 'upper' },
];

function karbDefaultsForRakenneosa(rakenneosa = '') {
  const lower = rakenneosa.toLowerCase();
  const match = KARB_RAKENNEOSA_DEFAULTS.find(r => lower.includes(r.match));
  return match
    ? { karb_upper_pinta: match.upper, karb_lower_pinta: match.lower, karb_to_table: match.karb_to_table }
    : { karb_upper_pinta: 'ulkopinta', karb_lower_pinta: 'sisäpinta', karb_to_table: 'upper' };
}


const TABLE_ROWS = [
  { key: 'nayte_tunnus',       label: 'Näytetunnus' },
  { key: 'rakenneosa',         label: 'Rakenneosa', multiline: true, isRakenneosa: true },
  { key: 'testi',              label: 'Testi', multiline: true, testiOptions: PORANÄYTE_TESTI_OPTIONS },
  { key: 'testaussyvyys',      label: 'Testaussyvyys\npinta/mm' },
  { key: 'naytteen_pinta',     label: 'Näytteen\nØ mm' },
  { key: 'terakseen_sijainti', label: 'Teräksen sijainti\nmm (Ø mm)' },
  { key: '__karb',             groupHeader: 'Karbonatisoitumissyvyys mm' },
  { key: '__karb_yp',          karbSlotHeader: true, karbSlot: 'upper' },
  { key: '__karb_yp_min',      karbStat: true, karbSlot: 'upper', stat: 'min', label: 'min' },
  { key: '__karb_yp_max',      karbStat: true, karbSlot: 'upper', stat: 'max', label: 'max' },
  { key: '__karb_yp_km',       karbStat: true, karbSlot: 'upper', stat: 'km',  label: 'k-m' },
  { key: '__karb_ap',          karbSlotHeader: true, karbSlot: 'lower' },
  { key: '__karb_ap_min',      karbStat: true, karbSlot: 'lower', stat: 'min', label: 'min' },
  { key: '__karb_ap_max',      karbStat: true, karbSlot: 'lower', stat: 'max', label: 'max' },
  { key: '__karb_ap_km',       karbStat: true, karbSlot: 'lower', stat: 'km',  label: 'k-m' },
  { key: 'lisahuomioita',      label: 'Lisähuomioita', multiline: true },
  { key: 'rakennepaksuus',     label: 'Rakennepaksuus\nmm' },
];

function Cell({ value, onChange, readOnly }) {
  const ref = useRef(null);
  const composing = useRef(false);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    if (el.textContent !== (value ?? '')) el.textContent = value ?? '';
  }, [value]);
  if (readOnly) {
    return (
      <span style={{ fontSize: '0.82rem', color: value ? '#212529' : '#adb5bd', fontStyle: value ? 'normal' : 'italic' }}>
        {value || '—'}
      </span>
    );
  }
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning
      onFocus={() => setFocused(true)}
      onBlur={e => { setFocused(false); onChange(e.currentTarget.textContent); }}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={e => { composing.current = false; onChange(e.currentTarget.textContent); }}
      onInput={e => { if (!composing.current) onChange(e.currentTarget.textContent); }}
      onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
      style={{
        outline: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word', hyphens: 'auto',
        minHeight: '1.4em', lineHeight: 1.5, fontSize: '0.82rem', fontFamily: 'inherit',
        margin: '-6px -8px', padding: '6px 8px', borderRadius: 2,
        background: focused ? '#eef4ff' : 'transparent',
        transition: 'background 0.1s',
      }}
    />
  );
}

const selectCellStyle = {
  width: '100%', border: 'none', outline: 'none',
  fontSize: '0.82rem', fontFamily: 'inherit', background: 'transparent',
};

function RakenneosaCell({ value, onChange }) {
  return <RakenneosaSelect value={value} onChange={onChange} style={selectCellStyle} />;
}

function TestiCell({ value, onChange, options }) {
  const selected = new Set((value || '').split(',').map(s => s.trim()).filter(Boolean));
  const toggle = (opt) => {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange([...next].join(', '));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '2px 0' }}>
      {(options ?? []).map(opt => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={selected.has(opt)} onChange={() => toggle(opt)}
            style={{ margin: 0, cursor: 'pointer', accentColor: '#04aa00' }} />
          {opt}
        </label>
      ))}
    </div>
  );
}

function RowIdBar({ naytteet, labelWidth }) {
  if (!naytteet.length) return null;
  return (
    <div style={{ display: 'flex', marginTop: 2 }}>
      <div style={{ width: labelWidth, flexShrink: 0, fontSize: '0.68rem', color: '#adb5bd', padding: '2px 8px', fontStyle: 'italic' }}>Näyte-ID</div>
      {naytteet.map(n => (
        <div key={n.id} title={`nayte_id: ${n.id}`}
          style={{ flex: 1, fontSize: '0.68rem', fontFamily: 'monospace', color: '#adb5bd', padding: '2px 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
          #{n.id}
        </div>
      ))}
    </div>
  );
}

function RenameInput({ value, onChange, onCommit, onCancel }) {
  return (
    <input autoFocus value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={e => { if (e.key === 'Enter') onCommit(); if (e.key === 'Escape') onCancel(); }}
      onClick={e => e.stopPropagation()}
      style={{ fontSize: '0.85rem', fontWeight: 700, border: 'none', borderBottom: '1px solid #495057', outline: 'none', background: 'transparent', color: '#212529', width: Math.max(60, value.length * 9) + 'px', padding: '0 2px' }}
    />
  );
}

export default function PoranayteBlock({ data, onChange, tableStartNumber, subsectionTitle, ptsProjectName, allViewValue, onSetAllView }) {
  let ctxKiinteisto = null;
  let ctxSections = null;
  let ctxShowToast = null;
  try {
    const ctx = useReportTemplateContext();
    ctxKiinteisto = ctx?.report?.id_kiinteisto ?? ctx?.rakennusData?.id_kiinteisto ?? null;
    ctxSections = ctx?.sections ?? null;
    ctxShowToast = ctx?.showToast ?? null;
  } catch {}
  const { rakennusData } = useRakennusData?.() ?? {};
  const id_kiinteisto = ctxKiinteisto ?? rakennusData?.id_kiinteisto ?? null;
  const linkedPtsId = findLinkedProjectId(ctxSections);

  const [projekt, setProjekt]             = useState(null);
  const [naytteetData, setNaytteetData]   = useState([]);
  const naytteetDataRef = useRef([]);
  const [activeNayteId, setActiveNayteId] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [nayteCreateError, setNayteCreateError] = useState(null);
  const allNaytteet = allViewValue ?? true;
  const [allProjektit, setAllProjektit]   = useState([]);
  const [activeProjektiId, setActiveProjektiId] = useState(data?.mittausProjektiId ?? null);

  const activeNayte = naytteetData.find(n => n.id === activeNayteId) ?? null;
  const activeIdx   = naytteetData.findIndex(n => n.id === activeNayteId);

  const [showSelector, setShowSelector]           = useState(false);
  const [ptsProjects, setPtsProjects]             = useState([]);
  const [selectedPtsId, setSelectedPtsId]         = useState(null);
  const [existingProjektit, setExistingProjektit] = useState([]);
  const [newProjectName, setNewProjectName]       = useState('');
  const [selectorTab, setSelectorTab]             = useState('new');

  const [renamingProject, setRenamingProject]           = useState(false);
  const [renameProjectValue, setRenameProjectValue]     = useState('');
  const [pendingDeleteProject, setPendingDeleteProject] = useState(false);
  const [pendingDeleteNayteId, setPendingDeleteNayteId] = useState(null);
  const [addModal, setAddModal]                         = useState(false);
  const [addModalRakenneosa, setAddModalRakenneosa]     = useState('');
  const [addModalTunnus, setAddModalTunnus]             = useState('');
  const [addModalCreating, setAddModalCreating]         = useState(false);

  const saveTimers = useRef({});
  const pendingSaveFields = useRef({});  // nayteId → latest fields waiting to be saved
  const karbSaveTimers = useRef({});
  const showToastRef = useRef(ctxShowToast);
  useEffect(() => { showToastRef.current = ctxShowToast; }, [ctxShowToast]);
  const scheduleSave = useCallback((nayteId, fields, afterSave) => {
    clearTimeout(saveTimers.current[nayteId]);
    pendingSaveFields.current[nayteId] = {
      ...pendingSaveFields.current[nayteId],
      ...fields,
    };
    saveTimers.current[nayteId] = setTimeout(() => {
      const payload = pendingSaveFields.current[nayteId];
      delete pendingSaveFields.current[nayteId];
      updateNayte(nayteId, payload)
        .then(() => {
          showToastRef.current?.({ show: true, message: 'Kappale päivitettiin onnistuneesti.', bg: 'success' });
          afterSave?.();
        })
        .catch(err => {
          console.error(err);
          showToastRef.current?.({ show: true, message: 'Tallennus epäonnistui', bg: 'danger' });
        });
    }, 1200);
  }, []);

  // Flush any unsaved nayte field changes when the component unmounts (e.g. page navigation/refresh)
  useEffect(() => {
    return () => {
      Object.entries(pendingSaveFields.current).forEach(([nayteId, fields]) => {
        clearTimeout(saveTimers.current[nayteId]);
        updateNayte(nayteId, fields).catch(() => {});
      });
    };
  }, []);

  useEffect(() => {
    setActiveProjektiId(data?.mittausProjektiId ?? null);
  }, [data?.mittausProjektiId]);

  useEffect(() => {
    const ptsId = data?.ptsProjectId;
    if (!ptsId) { setAllProjektit([]); return; }
    getMittausProjektitByPTS(ptsId, 'poranäyte')
      .then(res => {
        const list = res.data ?? [];
        setAllProjektit(list);
        if (list.length > 0 && !data?.mittausProjektiId) {
          setActiveProjektiId(list[0].id);
          onChange?.({ ...data, mittausProjektiId: list[0].id });
        }
      })
      .catch(() => setAllProjektit([]));
  }, [data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeProjektiId) { setProjekt(null); setNaytteetData([]); return; }
    setProjekt(null);
    setNaytteetData([]);
    setLoading(true);
    getMittausProjekti(activeProjektiId)
      .then(projRes => {
        const proj = projRes.data;
        setProjekt(proj);
        const ptsId = data?.ptsProjectId ?? proj.id_pts_project;
        if (ptsId) {
          if (!data?.ptsProjectId) onChange?.({ ...data, ptsProjectId: ptsId });
          getMittausProjektitByPTS(ptsId, 'poranäyte').then(r => setAllProjektit(r.data ?? [])).catch(() => {});
        }
        return getNaytteetByProjekti(activeProjektiId).catch(() => ({ data: [] }));
      })
      .then(naytteetRes => {
        const naytteet = naytteetRes?.data ?? [];
        setNaytteetData(naytteet);
        naytteetListSet(activeProjektiId, naytteet);
        setActiveNayteId(naytteet[0]?.id ?? null);
        // Backfill: re-sync naytes that have testi but may be missing measurement entries,
        // then refresh peitepaksuus project so newly created kohteet are visible.
        Promise.all(naytteet.filter(n => n.testi).map(n => syncNayte(n.id).catch(() => {})))
          .then(() => refreshPeitepaksuusProjekti());
      })
      .catch(() => setProjekt(null))
      .finally(() => setLoading(false));
  }, [activeProjektiId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id_kiinteisto) return;
    getDynamicPTSProjects(id_kiinteisto).then(res => setPtsProjects(res.data ?? [])).catch(() => {});
  }, [id_kiinteisto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!linkedPtsId || linkedPtsId === data?.ptsProjectId) return;
    onChange?.({ ...data, ptsProjectId: linkedPtsId, mittausProjektiId: null });
  }, [linkedPtsId, data?.ptsProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSelector = async () => {
    if (!id_kiinteisto) return;
    const res = await getDynamicPTSProjects(id_kiinteisto);
    const pts = res.data ?? [];
    setPtsProjects(pts);
    setSelectedPtsId(pts[0]?.id ?? null);
    setExistingProjektit([]);
    setNewProjectName(defaultProjectName('Poranäytteet', subsectionTitle));
    setSelectorTab('new');
    if (pts[0]?.id) {
      const r2 = await getMittausProjektitByPTS(pts[0].id, 'poranäyte');
      setExistingProjektit(r2.data ?? []);
    }
    setShowSelector(true);
  };

  const onPtsChange = async (ptsId) => {
    setSelectedPtsId(ptsId);
    const res = await getMittausProjektitByPTS(ptsId, 'poranäyte');
    setExistingProjektit(res.data ?? []);
  };

  const refreshAllProjektit = (ptsId) =>
    getMittausProjektitByPTS(ptsId, 'poranäyte')
      .then(res => setAllProjektit(res.data ?? []))
      .catch(() => {});

  const createProject = async () => {
    if (!selectedPtsId) return;
    const name = newProjectName.trim() || defaultProjectName('Poranäytteet', subsectionTitle);
    const res = await createMittausProjekti(selectedPtsId, 'poranäyte', name);
    const proj = res.data;
    setProjekt(proj);
    setNaytteetData([]);
    setActiveNayteId(null);
    setActiveProjektiId(proj.id);
    onChange?.({ ...data, mittausProjektiId: proj.id, ptsProjectId: selectedPtsId });
    refreshAllProjektit(selectedPtsId);
    setShowSelector(false);
  };

  const linkProject = async (id) => {
    const [projRes, naytteetRes] = await Promise.all([
      getMittausProjekti(id),
      getNaytteetByProjekti(id),
    ]);
    const proj = projRes.data;
    const naytteet = naytteetRes.data ?? [];
    setProjekt(proj);
    setNaytteetData(naytteet);
    naytteetListSet(id, naytteet);
    setActiveNayteId(naytteet[0]?.id ?? null);
    setActiveProjektiId(id);
    onChange?.({ ...data, mittausProjektiId: id, ptsProjectId: selectedPtsId });
    refreshAllProjektit(selectedPtsId);
    setShowSelector(false);
  };

  const commitRenameProject = async () => {
    const trimmed = renameProjectValue.trim();
    if (!trimmed || !projekt) { setRenamingProject(false); return; }
    await renameMittausProjekti(projekt.id, trimmed);
    setProjekt(p => ({ ...p, nimi: trimmed }));
    setRenamingProject(false);
  };

  const confirmDeleteProject = async () => {
    if (!projekt) return;
    await deleteMittausProjektiWithSiblings(projekt.id);
    const remaining = allProjektit.filter(p => p.id !== projekt.id);
    setAllProjektit(remaining);
    setProjekt(null);
    setNaytteetData([]);
    setActiveNayteId(null);
    const nextId = remaining[0]?.id ?? null;
    setActiveProjektiId(nextId);
    onChange?.({ ...data, mittausProjektiId: projekt.id === data?.mittausProjektiId ? (nextId ?? null) : data?.mittausProjektiId });
    setPendingDeleteProject(false);
  };

  const openAddModal = () => { setAddModalRakenneosa(''); setAddModalTunnus(''); setAddModal(true); };
  const closeAddModal = () => { if (!addModalCreating) setAddModal(false); };
  const submitAddModal = async () => {
    const name = addModalRakenneosa.trim();
    if (!name || !addModalTunnus.trim()) return;
    setAddModalCreating(true);
    try { await handleNayteCreate(name, false, addModalTunnus.trim()); setAddModal(false); }
    catch { /* duplicate-tunnus error already shown via nayteCreateError */ }
    finally { setAddModalCreating(false); }
  };

  const handleNayteCreate = async (name, _withNayte = false, tunnus = '') => {
    if (tunnus && naytteetData.some(n => n.nayte_tunnus === tunnus)) {
      setNayteCreateError('Näytetunnus on jo olemassa. Voit lisätä testejä näytteelle poranäyte taulukosta.');
      throw Object.assign(new Error('duplicate'), { code: 'DUPLICATE_TUNNUS' });
    }
    const res = await createNayte(projekt.id, {
      rakenneosa: name,
      nayte_tunnus: tunnus,
      naytteen_pinta: '55',
      sort_order: naytteetData.length,
    });
    const nayte = res.data;
    const next = [...naytteetData, nayte];
    setNaytteetData(next);
    naytteetListSet(projekt.id, next);
    setActiveNayteId(nayte.id);

    // Backend creates the peitepaksuus kohde (and any testi-specific kohteet) server-side.
    syncNayte(nayte.id)
      .then(() => refreshPeitepaksuusProjekti())
      .catch(console.error);

    return nayte;
  };

  const handleNayteDelete = async (id) => {
    await deleteNayte(id); // backend cascades to linked mittaus_kohteet
    setNaytteetData(prev => {
      const remaining = prev.filter(n => n.id !== id);
      naytteetListSet(activeProjektiId, remaining);
      if (activeNayteId === id) setActiveNayteId(remaining[0]?.id ?? null);
      return remaining;
    });
  };

  useEffect(() => { naytteetDataRef.current = naytteetData; }, [naytteetData]);

  const updateNayteField = useCallback((nayteId, field, value) => {
    if (field === 'testi' && value) {
      // Let the backend create missing measurement kohteet after the save lands
      scheduleSave(nayteId, { [field]: value }, async () => {
        await syncNayte(nayteId).catch(() => {});
        await refreshPeitepaksuusProjekti();
      });
    } else {
      scheduleSave(nayteId, { [field]: value });
    }
    setNaytteetData(prev => {
      const updated = prev.map(n => n.id === nayteId ? { ...n, [field]: value } : n);
      naytteetListSet(activeProjektiId, updated);
      if (field === 'nayte_tunnus') {
        broadcastNayteTunnus(nayteId, value);
      }
      return updated;
    });
  }, [scheduleSave, activeProjektiId]);

  // Live karb stats from PeitepaksuusBlock via naytteetCache
  const nayteIdsKey = useMemo(() => naytteetData.map(n => n.id).join(','), [naytteetData]);
  useEffect(() => {
    if (!naytteetData.length) return;
    const unsubs = naytteetData.map(n =>
      naytteetCacheSubscribe(n.id, (patch) => {
        setNaytteetData(prev => prev.map(x => x.id === n.id ? { ...x, ...patch } : x));
      })
    );
    return () => unsubs.forEach(fn => fn());
  }, [nayteIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Peitepaksuus project: load to get kohde surface labels + karb_to_table selection
  const [peitepaksuusProjekti, setPeitepaksuusProjekti] = useState(null);
  const peitepaksuusProjektiRef = useRef(null);
  useEffect(() => { peitepaksuusProjektiRef.current = peitepaksuusProjekti; }, [peitepaksuusProjekti]);

  // Re-fetch peitepaksuus project from API and update local state + cache.
  // Called after syncNayte so newly created kohteet are visible immediately.
  const refreshPeitepaksuusProjekti = useCallback(async () => {
    const pid = peitepaksuusProjektiRef.current?.id;
    if (!pid) return;
    try {
      const refreshed = (await getMittausProjekti(pid)).data;
      mittausCacheSet(refreshed.id, refreshed);
      setPeitepaksuusProjekti(refreshed);
    } catch {}
  }, []);
  useEffect(() => {
    const myMittausId = data?.mittausProjektiId;
    if (!myMittausId) { setPeitepaksuusProjekti(null); return; }
    // Backend finds the sibling peitepaksuus project with the same PTS project and name.
    getSiblingMittausProjekti(myMittausId, 'peitepaksuus')
      .then(r => setPeitepaksuusProjekti(r.data))
      .catch(() => setPeitepaksuusProjekti(null));
  }, [data?.mittausProjektiId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep peitepaksuusProjekti in sync when syncPoranayteRow adds a new kohde to the cache.
  useEffect(() => {
    const id = peitepaksuusProjekti?.id;
    if (!id) return;
    return mittausCacheSubscribe(id, setPeitepaksuusProjekti);
  }, [peitepaksuusProjekti?.id]);

  const activeKohde = useMemo(
    () => peitepaksuusProjekti?.kohteet?.find(k => k.nimi === activeNayte?.rakenneosa) ?? null,
    [peitepaksuusProjekti, activeNayte?.rakenneosa] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const kohdeByRakenneosa = useMemo(() => {
    const m = {};
    (peitepaksuusProjekti?.kohteet ?? []).forEach(k => { if (k.nimi) m[k.nimi] = k; });
    return m;
  }, [peitepaksuusProjekti]);

  const updateKohdeKarbField = useCallback((kohde, field, value) => {
    if (!kohde) return;
    const newData = { ...kohde.data, [field]: value };
    updateMittausKohde(kohde.id, { data: newData }).catch(console.error);
    setPeitepaksuusProjekti(p => p ? {
      ...p,
      kohteet: p.kohteet.map(k => k.id === kohde.id ? { ...k, data: newData } : k),
    } : p);
  }, []);

  // Upsert one nayte's karb data into kohde.data.karb_per_nayte[tunnus][field].
  // Also syncs the selected k-m values into carbonation_measurements so the
  // PeitepaksuusBlock input table auto-populates.
  const upsertKarbPerNayte = useCallback((kohde, tunnus, field, value, rakenneosa = '') => {
    if (!kohde || !tunnus) return;
    const prev = kohde.data?.karb_per_nayte ?? {};
    const karb_per_nayte = { ...prev, [tunnus]: { ...(prev[tunnus] ?? {}), [field]: value } };
    const defaultToTable = karbDefaultsForRakenneosa(rakenneosa).karb_to_table;
    // Derive flat km array + parallel label array for the input table
    const entries = Object.entries(karb_per_nayte)
      .map(([t, d]) => ({ t, km: ((d.to_table ?? defaultToTable) === 'lower' ? d.lower_km : d.upper_km) ?? null }))
      .filter(e => e.km != null);
    const newData = {
      ...kohde.data,
      karb_per_nayte,
      carbonation_measurements: { alapinta: entries.map(e => e.km), ylapinta: [] },
      karb_tunnus_labels: entries.map(e => e.t),
    };
    // Update local state immediately for instant UI feedback; debounce the API write
    // so rapid field changes (tab-key navigation) collapse into one request.
    setPeitepaksuusProjekti(p => {
      if (!p) return p;
      const updated = { ...p, kohteet: p.kohteet.map(k => k.id === kohde.id ? { ...k, data: newData } : k) };
      mittausCacheSet(updated.id, updated);
      return updated;
    });
    clearTimeout(karbSaveTimers.current[kohde.id]);
    karbSaveTimers.current[kohde.id] = setTimeout(() => {
      updateMittausKohde(kohde.id, { data: newData }).catch(console.error);
    }, 600);
  }, []);

  // Local state for karb toggle + pinta — onChange doesn't update data prop immediately
  // (setContent is not called after handleBlockChange), so we need local state for instant UI feedback.
  const [karbSettings, setKarbSettings] = useState(() => data?.karbSettings ?? {});

  // Re-sync from server when data prop changes, but local optimistic state wins.
  // A stale save (from another block) can land without karbSettings and reset data —
  // merging per-nayte ensures the local toggle survives until the correct save lands.
  useEffect(() => {
    setKarbSettings(prev => {
      const server = data?.karbSettings ?? {};
      const merged = { ...server };
      for (const [id, localPerNayte] of Object.entries(prev)) {
        merged[id] = { ...(server[id] ?? {}), ...localPerNayte };
      }
      return merged;
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const getKarbNayteSettings = (nayteId, rakenneosa = '') => {
    const saved = karbSettings[nayteId] ?? {};
    const defaults = karbDefaultsForRakenneosa(rakenneosa);
    // Only trust saved pinta values if they were saved against the same rakenneosa.
    // Old saves lack the rakenneosa key, so they are treated as stale and ignored.
    const pintaFromSaved = saved.rakenneosa === rakenneosa && rakenneosa;
    return {
      karb_upper_pinta: pintaFromSaved ? (saved.karb_upper_pinta ?? defaults.karb_upper_pinta) : defaults.karb_upper_pinta,
      karb_lower_pinta: pintaFromSaved ? (saved.karb_lower_pinta ?? defaults.karb_lower_pinta) : defaults.karb_lower_pinta,
      karb_to_table: saved.karb_to_table ?? defaults.karb_to_table,
    };
  };

  const updateKarbNayteSetting = useCallback((nayteId, field, value) => {
    const rakenneosa = naytteetData.find(n => n.id === nayteId)?.rakenneosa ?? '';
    const defaults = karbDefaultsForRakenneosa(rakenneosa);
    const current = {
      ...defaults,
      rakenneosa,
      ...(karbSettings[nayteId]?.rakenneosa === rakenneosa ? karbSettings[nayteId] : { rakenneosa }),
    };
    const next = { ...karbSettings, [nayteId]: { ...current, [field]: value } };
    setKarbSettings(next);
    onChange?.({ ...data, karbSettings: next });
  }, [karbSettings, data, onChange]); // eslint-disable-line react-hooks/exhaustive-deps


  // Nayte added by another block (e.g. peitepaksuus "Luo poranäyte?")
  useEffect(() => {
    if (!activeProjektiId) return;
    return naytteetCacheAddSubscribe(activeProjektiId, (nayte) => {
      setNaytteetData(prev => {
        if (prev.some(n => n.id === nayte.id)) return prev;
        return [...prev, nayte];
      });
    });
  }, [activeProjektiId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!id_kiinteisto) return (
    <div>
      <div className="text-muted small p-2">Kiinteistön ID ei saatavilla.</div>
      <TaulukkoFooter taulukkoNum={tableStartNumber ?? 1} kohdeId="preview" caption={null} onChange={() => {}} />
    </div>
  );
  if (loading) return <div className="text-muted small p-2">Ladataan…</div>;

  const taulukkoNum = tableStartNumber ?? (activeIdx + 1);
  const nayteLabel = (n) => {
    const parts = [n.rakenneosa, n.nayte_tunnus].filter(Boolean);
    return parts.join(' — ') || `Näyte ${n.id}`;
  };
  const naytteetAsTabs = naytteetData.map(n => ({ id: n.id, nimi: nayteLabel(n) }));

  const renderCell = (nayte, rowDef) => {
    let val = String(nayte[rowDef.key] ?? '');
    if (rowDef.readOnly) {
      const n = parseFloat(val);
      if (!isNaN(n)) val = String(n);
    }
    if (rowDef.isRakenneosa) {
      return <RakenneosaCell value={val} onChange={v => updateNayteField(nayte.id, rowDef.key, v)} />;
    }
    if (rowDef.testiOptions) {
      return <TestiCell options={rowDef.testiOptions} value={val} onChange={v => updateNayteField(nayte.id, rowDef.key, v)} />;
    }
    return <Cell value={val} readOnly={rowDef.readOnly} onChange={v => updateNayteField(nayte.id, rowDef.key, v)} />;
  };

  return (
    <div style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.95rem' }}>
      {(ptsProjectName || data?.ptsProjectId) && (
        <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: 6 }}>
          {ptsProjectName ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.name ?? ptsProjects.find(p => p.id === data?.ptsProjectId)?.nimi ?? `PTS ${data.ptsProjectId}`}
        </div>
      )}
      {/* ── Project header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#6c757d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Tarkastuskohde:</span>
        {allProjektit.length > 0 ? (
          <>
            {allProjektit.map(p => (
              <button key={p.id} type="button" onClick={() => { setActiveProjektiId(p.id); onChange?.({ ...data, mittausProjektiId: p.id }); }}
                style={{ padding: '2px 8px', fontSize: '0.78rem', border: '1px solid', borderRadius: 3, cursor: 'pointer',
                  borderColor: p.id === activeProjektiId ? '#198754' : '#dee2e6',
                  background: p.id === activeProjektiId ? '#f0fdf4' : '#fff',
                  color: p.id === activeProjektiId ? '#15803d' : '#6c757d',
                  fontWeight: p.id === activeProjektiId ? 600 : 400 }}>
                {p.nimi}
              </button>
            ))}
            {projekt && (
              <button type="button" onClick={() => setPendingDeleteProject(true)}
                style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} title="Poista aktiivinen tarkastuskohde">×</button>
            )}
          </>
        ) : projekt ? (
          <>
            {renamingProject ? (
              <RenameInput value={renameProjectValue} onChange={setRenameProjectValue}
                onCommit={commitRenameProject} onCancel={() => setRenamingProject(false)} />
            ) : (
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}
                onDoubleClick={() => { setRenameProjectValue(projekt.nimi); setRenamingProject(true); }}>
                {projekt.nimi}
              </span>
            )}
            <button type="button" onClick={() => setPendingDeleteProject(true)}
              style={{ background: 'none', border: 'none', color: '#adb5bd', cursor: 'pointer', fontSize: '1rem', padding: 0, lineHeight: 1 }} title="Poista tarkastuskohde">×</button>
          </>
        ) : (
          <button type="button" onClick={openSelector}
            style={{ padding: '3px 10px', borderRadius: 20, border: '1px dashed #0d6efd', background: '#fff', color: '#0d6efd', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
            + Valitse / luo tarkastuskohde
          </button>
        )}
      </div>

      {projekt && (
        <>
          {/* ── Nayte tabs + toggle ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <KohdeTabBar
                kohteet={naytteetAsTabs}
                activeKohdeId={activeNayteId}
                tableStartNumber={tableStartNumber != null ? tableStartNumber : undefined}
                onActivate={id => { setActiveNayteId(id); onSetAllView?.(false); }}
                onAdd={openAddModal}
                addLabel="+ Lisää näyte"
                onRename={(id, name) => updateNayteField(id, 'rakenneosa', name)}
                onDelete={id => setPendingDeleteNayteId(id)}
                hideTabs={allNaytteet}
              />
            </div>
            <button
              type="button"
              onClick={() => onSetAllView?.(!allNaytteet)}
              title={allNaytteet ? 'Näytä välilehdet' : 'Näytä kaikki rinnakkain'}
              style={{ flexShrink: 0, padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, background: allNaytteet ? '#198754' : '#fff', color: allNaytteet ? '#fff' : '#6c757d', fontSize: '1rem', cursor: 'pointer', marginTop: 2 }}
            >⇄</button>
          </div>

          {addModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={closeAddModal}>
              <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(520px, 95vw)', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
                onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Lisää näyte</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d', marginBottom: 8 }}>Rakenneosa <span style={{ color: '#dc3545' }}>*</span></div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8 }}>
                      {JULKISIVU_RAKENNEOSA_OPTIONS.map(opt => {
                        const sel = addModalRakenneosa === opt;
                        return (
                          <button key={opt} type="button" onClick={() => setAddModalRakenneosa(opt)}
                            style={{ padding: '5px 12px', borderRadius: 20, border: '1.5px solid', fontSize: '0.82rem', cursor: 'pointer', textAlign: 'left',
                              fontWeight: sel ? 700 : 400,
                              borderColor: sel ? '#198754' : '#dee2e6',
                              background: sel ? '#f0fdf4' : '#fafafa',
                              color: sel ? '#15803d' : '#495057' }}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    <input type="text" placeholder="Tai kirjoita…"
                      value={addModalRakenneosa}
                      onChange={e => setAddModalRakenneosa(e.target.value)}
                      style={{ width: '100%', border: '1px solid #ced4da', borderRadius: 4, padding: '5px 9px', fontSize: '0.85rem', outline: 'none' }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignSelf: 'stretch' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6c757d', marginBottom: 8 }}>Näytetunnus <span style={{ color: '#dc3545' }}>*</span></div>
                    <input autoFocus type="text" placeholder="esim. P1"
                      value={addModalTunnus}
                      onChange={e => setAddModalTunnus(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && addModalRakenneosa.trim() && addModalTunnus.trim()) submitAddModal(); if (e.key === 'Escape') closeAddModal(); }}
                      style={{ width: '100%', border: '1.5px solid #ced4da', borderRadius: 6, padding: '10px 14px', fontSize: '1.1rem', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <span style={{ fontSize: '0.72rem', color: '#6c757d', marginRight: 'auto', alignSelf: 'center' }}><span style={{ color: '#dc3545' }}>*</span> Pakollinen tieto</span>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeAddModal} disabled={addModalCreating}>Peruuta</button>
                  <button type="button" className="btn btn-sm btn-success" onClick={submitAddModal} disabled={addModalCreating || !addModalRakenneosa.trim() || !addModalTunnus.trim()}>
                    {addModalCreating ? <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} /> : 'Lisää'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {nayteCreateError && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setNayteCreateError(null)}>
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 92vw)', padding: '28px 28px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#fff3cd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>⚠️</div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>Näytetunnus on jo olemassa</div>
                <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5 }}>Voit lisätä testejä näytteelle poranäyte taulukosta.</div>
                <button type="button" className="btn btn-warning btn-sm mt-1" onClick={() => setNayteCreateError(null)} style={{ minWidth: 80 }}>OK</button>
              </div>
            </div>
          )}

          <div lang="fi">
            {allNaytteet ? (
              naytteetData.length > 0 && (
                <>
                  <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={CL}></th>
                        {naytteetData.map(n => (
                          <th key={n.id} style={{ ...CH, whiteSpace: 'nowrap' }}>
                            {nayteLabel(n)}
                            <span
                              role="button"
                              onClick={() => setPendingDeleteNayteId(n.id)}
                              style={{ marginLeft: 6, color: 'rgba(255,255,255,0.65)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, verticalAlign: 'middle' }}
                              title="Poista näyte"
                            >×</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {TABLE_ROWS.map(rowDef => {
                        if (rowDef.groupHeader) {
                          return (
                            <tr key={rowDef.key}>
                              <td colSpan={naytteetData.length + 1} style={CG}>{rowDef.groupHeader}</td>
                            </tr>
                          );
                        }
                        if (rowDef.karbSlotHeader) {
                          const pintaField = rowDef.karbSlot === 'upper' ? 'karb_upper_pinta' : 'karb_lower_pinta';
                          return (
                            <tr key={rowDef.key}>
                              <td style={CL}></td>
                              {naytteetData.map(n => {
                                const settings = getKarbNayteSettings(n.id, n.rakenneosa);
                                const isForTable = settings.karb_to_table === rowDef.karbSlot;
                                return (
                                  <td key={n.id} style={{ ...CG2, padding: '3px 6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span
                                        onClick={() => { updateKarbNayteSetting(n.id, 'karb_to_table', rowDef.karbSlot); const k = kohdeByRakenneosa[n.rakenneosa]; if (k) upsertKarbPerNayte(k, n.nayte_tunnus || n.rakenneosa, 'to_table', rowDef.karbSlot, n.rakenneosa); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: isForTable ? '#fff' : 'rgba(255,255,255,0.45)', fontWeight: isForTable ? 700 : 400, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                      >
                                        <span style={{ width: 13, height: 13, flexShrink: 0, borderRadius: 3, border: `2px solid ${isForTable ? '#7dff7a' : 'rgba(255,255,255,0.4)'}`, background: isForTable ? '#7dff7a' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.12s, border-color 0.12s' }}>
                                          {isForTable && <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><polyline points="1,4 3,6.5 7,1.5" stroke="#1a6b00" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                        </span>
                                        KUVAAJA
                                      </span>
                                      <select
                                        value={settings[pintaField]}
                                        onChange={e => updateKarbNayteSetting(n.id, pintaField, e.target.value)}
                                        style={{ flex: 1, padding: '1px 2px', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 3, background: 'rgba(0,0,0,0.25)', color: '#fff', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                                      >
                                        {(rowDef.karbSlot === 'upper' ? KARB_PINTA_OPTIONS_UPPER : KARB_PINTA_OPTIONS_LOWER).map(opt => (
                                          <option key={opt} value={opt} style={{ background: '#1a1a1a', color: '#fff' }}>{opt}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        }
                        if (rowDef.karbStat) {
                          const dataField  = `karb_${rowDef.karbSlot}_${rowDef.stat}`;
                          const cacheField = rowDef.karbSlot === 'upper' ? `karb_yp_${rowDef.stat}` : `karb_ap_${rowDef.stat}`;
                          return (
                            <tr key={rowDef.key}>
                              <td style={CL}>{rowDef.label}</td>
                              {naytteetData.map(n => {
                                const k = kohdeByRakenneosa[n.rakenneosa];
                                const localVal = karbSettings[n.id]?.[cacheField];
                                const tunnus = n.nayte_tunnus || n.rakenneosa;
                                const storedKm = rowDef.stat === 'km' ? k?.data?.karb_per_nayte?.[tunnus]?.[`${rowDef.karbSlot}_km`] : undefined;
                                const initVal = localVal != null ? localVal : (storedKm ?? n[cacheField] ?? '');
                                return (
                                  <td key={n.id} style={C}>
                                    <input
                                      key={`${n.id}_${rowDef.key}`}
                                      type="text"
                                      inputMode="decimal"
                                      defaultValue={initVal !== null && initVal !== undefined ? String(initVal) : ''}
                                      onBlur={e => {
                                        const num = e.target.value.trim() === '' ? null : parseFloat(e.target.value.replace(',', '.'));
                                        updateKarbNayteSetting(n.id, cacheField, num ?? '');
                                        if (k && rowDef.stat === 'km') {
                                          upsertKarbPerNayte(k, tunnus, `${rowDef.karbSlot}_km`, num, n.rakenneosa);
                                        }
                                      }}
                                      style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: 'inherit' }}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        }
                        return (
                          <tr key={rowDef.key}>
                            <td style={CL}>{rowDef.label}</td>
                            {naytteetData.map(n => (
                              <td key={n.id} style={C}>
                                {renderCell(n, rowDef)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <RowIdBar naytteet={naytteetData} labelWidth={CL.width} />
                </>
              )
            ) : (
              activeNayte && (
                <>
                  <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
                    <tbody>
                      {TABLE_ROWS.map(rowDef => {
                        if (rowDef.groupHeader) {
                          return (
                            <tr key={rowDef.key}>
                              <td colSpan={2} style={CG}>{rowDef.groupHeader}</td>
                            </tr>
                          );
                        }
                        if (rowDef.karbSlotHeader) {
                          const pintaField = rowDef.karbSlot === 'upper' ? 'karb_upper_pinta' : 'karb_lower_pinta';
                          const activeNayte = naytteetData.find(n => n.id === activeNayteId);
                          const settings = getKarbNayteSettings(activeNayteId, activeNayte?.rakenneosa);
                          const isForTable = settings.karb_to_table === rowDef.karbSlot;
                          return (
                            <tr key={rowDef.key}>
                              <td colSpan={2} style={CG2}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span
                                    onClick={() => { updateKarbNayteSetting(activeNayteId, 'karb_to_table', rowDef.karbSlot); if (activeKohde) upsertKarbPerNayte(activeKohde, activeNayte?.nayte_tunnus || activeNayte?.rakenneosa, 'to_table', rowDef.karbSlot, activeNayte?.rakenneosa); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: isForTable ? '#fff' : 'rgba(255,255,255,0.45)', fontWeight: isForTable ? 700 : 400, cursor: 'pointer', userSelect: 'none' }}
                                  >
                                    <span style={{ width: 15, height: 15, flexShrink: 0, borderRadius: 3, border: `2px solid ${isForTable ? '#7dff7a' : 'rgba(255,255,255,0.4)'}`, background: isForTable ? '#7dff7a' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.12s, border-color 0.12s' }}>
                                      {isForTable && <svg width="9" height="9" viewBox="0 0 9 9" fill="none"><polyline points="1,4.5 3.5,7 8,1.5" stroke="#1a6b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </span>
                                    KUVAAJA
                                  </span>
                                  <select
                                    value={settings[pintaField]}
                                    onChange={e => updateKarbNayteSetting(activeNayteId, pintaField, e.target.value)}
                                    style={{ padding: '1px 4px', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 3, background: 'rgba(0,0,0,0.25)', color: '#fff', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}
                                  >
                                    {(rowDef.karbSlot === 'upper' ? KARB_PINTA_OPTIONS_UPPER : KARB_PINTA_OPTIONS_LOWER).map(opt => (
                                      <option key={opt} value={opt} style={{ background: '#1a1a1a', color: '#fff' }}>{opt}</option>
                                    ))}
                                  </select>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                        if (rowDef.karbStat) {
                          const dataField  = `karb_${rowDef.karbSlot}_${rowDef.stat}`;
                          const cacheField = rowDef.karbSlot === 'upper' ? `karb_yp_${rowDef.stat}` : `karb_ap_${rowDef.stat}`;
                          const localVal   = karbSettings[activeNayteId]?.[cacheField];
                          const activeTunnus = activeNayte?.nayte_tunnus || activeNayte?.rakenneosa;
                          const storedKm = rowDef.stat === 'km' ? activeKohde?.data?.karb_per_nayte?.[activeTunnus]?.[`${rowDef.karbSlot}_km`] : undefined;
                          const initVal    = localVal != null ? localVal : (storedKm ?? activeNayte?.[cacheField] ?? '');
                          return (
                            <tr key={rowDef.key}>
                              <td style={CL}>{rowDef.label}</td>
                              <td style={C}>
                                <input
                                  key={`${activeNayteId}_${rowDef.key}`}
                                  type="text"
                                  inputMode="decimal"
                                  defaultValue={initVal !== null && initVal !== undefined ? String(initVal) : ''}
                                  onBlur={e => {
                                    const num = e.target.value.trim() === '' ? null : parseFloat(e.target.value.replace(',', '.'));
                                    updateKarbNayteSetting(activeNayteId, cacheField, num ?? '');
                                    if (activeKohde && rowDef.stat === 'km') {
                                      upsertKarbPerNayte(activeKohde, activeTunnus, `${rowDef.karbSlot}_km`, num, activeNayte?.rakenneosa);
                                    }
                                  }}
                                  style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: 'inherit' }}
                                />
                              </td>
                            </tr>
                          );
                        }
                        return (
                          <tr key={rowDef.key}>
                            <td style={CL}>{rowDef.label}</td>
                            <td style={C}>{renderCell(activeNayte, rowDef)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <RowIdBar naytteet={[activeNayte]} labelWidth={CL.width} />
                </>
              )
            )}
          </div>
        </>
      )}

      <TaulukkoFooter
        taulukkoNum={taulukkoNum}
        kohdeId={activeNayteId?.toString() ?? 'preview'}
        caption={data?.caption}
        onChange={capData => onChange?.({ ...data, caption: capData })}
      />

      {/* ── Project selector overlay ── */}
      {showSelector && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowSelector(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(480px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Poranäyte-tarkastuskohde</div>
            <div>
              <label style={{ fontSize: '0.82rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, display: 'block' }}>Dynamic PTS -projekti</label>
              <select className="form-select form-select-sm" value={selectedPtsId ?? ''} onChange={e => onPtsChange(Number(e.target.value))}>
                {ptsProjects.map(p => <option key={p.id} value={p.id}>{p.name ?? p.nimi}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6' }}>
              {['new', 'existing'].map(tab => (
                <button key={tab} type="button" onClick={() => setSelectorTab(tab)}
                  style={{ padding: '6px 16px', border: 'none', borderBottom: tab === selectorTab ? '2px solid #0d6efd' : '2px solid transparent', background: 'none', color: tab === selectorTab ? '#0d6efd' : '#6c757d', fontWeight: tab === selectorTab ? 600 : 400, fontSize: '0.88rem', cursor: 'pointer' }}>
                  {tab === 'new' ? 'Uusi tarkastuskohde' : `Olemassa olevat (${existingProjektit.length})`}
                </button>
              ))}
            </div>
            {selectorTab === 'new' ? (
              <>
                <input className="form-control form-control-sm" autoFocus placeholder="Tarkastuskohteen nimi…" value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && selectedPtsId) createProject(); if (e.key === 'Escape') setShowSelector(false); }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Peruuta</button>
                  <button className="btn btn-sm btn-success" onClick={createProject} disabled={!selectedPtsId}>Luo tarkastuskohde</button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {existingProjektit.length === 0
                  ? <div style={{ fontSize: '0.88rem', color: '#6c757d' }}>Ei poranäyte-tarkastuskohteita tässä PTS-projektissa.</div>
                  : existingProjektit.map(p => (
                    <button key={p.id} type="button" onClick={() => linkProject(p.id)}
                      style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p.nimi}
                    </button>
                  ))}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowSelector(false)}>Sulje</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete nayte confirmation ── */}
      {pendingDeleteNayteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteNayteId(null); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista näyte</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>
              {(() => {
                const n = naytteetData.find(x => x.id === pendingDeleteNayteId);
                return <>Haluatko poistaa näytteen <strong>{n?.nayte_tunnus || n?.rakenneosa || `#${pendingDeleteNayteId}`}</strong>?</>;
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteNayteId(null)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={async () => { const id = pendingDeleteNayteId; setPendingDeleteNayteId(null); await handleNayteDelete(id); }}>Poista</button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteProject && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteProject(false); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista tarkastuskohde</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>Haluatko poistaa tarkastuskohteen <strong>{projekt?.nimi}</strong> kaikkine kohteineen?</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteProject(false)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={confirmDeleteProject}>Poista</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
